import { useState, useEffect, useRef } from "react";
import { Alert, Button, GeolocationInput } from "../components";
import { usePageLoadingContext } from "../contexts/PageLoadingContext";
import { useClientConfigs } from "../contexts/ClientConfigContext";
import { useSelector } from "react-redux";
import store, { RootState } from "../store";
import { updateQuote } from "../store/quote/action";
import { GeicoPageNames, useAnalytics } from "../contexts/AnalyticsContext";
import { StateLookupRequest, STATE_LOOKUP_RESPONSE } from "../api/types";
import { digitsOnly } from "../lib/text-helpers";
import { useApi } from "../api";
import { useAutoRedirectContext } from "../contexts/AutoRedirectContext";
import { BASE_APP_PAth, API_ERROR_MESSAGE } from "../api/launchpad-api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { storage } from "../lib/storage";
import { v4 as uuidv4 } from 'uuid';

interface ZipSelectionComponentProps {
  showAlert?: boolean;
  setShowAlert?: (show: boolean) => void;
  alertMessage?: { title: string; message: string };
  setAlertMessage?: (message: { title: string; message: string }) => void;
}

export const ZipSelectionComponent: React.FC<ZipSelectionComponentProps> = ({
  showAlert = false,
  setShowAlert = () => { },
  alertMessage = { title: "", message: "" },
  setAlertMessage = () => { },
}) => {
  const api = useApi();
  const navigate = useNavigate();
  const quote = useSelector((state: RootState) => state.quote.quote);
  const { showLoader, hideLoader } = usePageLoadingContext();
  const clientConfig = useClientConfigs();
  const { geicoDtm, setGeicoDtm } = useAnalytics();
  const [searchParams] = useSearchParams();
  const prefilledZipCode = searchParams.get("zip");
  const zipCode = quote.data.Zip;
  const [model, setModel] = useState({ zip: quote.data.Zip, stateCode: quote.data.StateCode });
  const unitedState = quote.data.StateCode;
  const [zipCodeError, setZipCodeError] = useState("");
  const refZipCodeUseRef = useRef<HTMLInputElement>();
  const redirectContext = useAutoRedirectContext();
  const zipSelectionPageName = GeicoPageNames.Launchpad + ":Zip-Selection";

  useEffect(() => {
    const initialZip = prefilledZipCode || (storage.exist("searchQuoteZip") ? storage.load("searchQuoteZip") : "");
    quote.data.Zip = initialZip;
    setModel({ zip: initialZip, stateCode: quote.data.StateCode });
  }, []);

  useEffect(() => {
    try {
      showLoader();
      setGeicoDtm({ ...geicoDtm, viewName: zipSelectionPageName });
      setModel({ ...model, zip: quote.data.Zip, stateCode: quote.data.StateCode });
    } catch (err) {
      console.error(err);
      hideLoader();
    }
  }, []);

  useEffect(() => {
    if (redirectContext.verificationDone) {
      hideLoader();
    }
  }, [redirectContext.verificationDone]);

  useEffect(() => {
    if (redirectContext.verificationDone) {
      stateLookup();
    }
  }, [zipCode]);

  useEffect(() => {
    if (redirectContext.verificationDone) {
      setGeicoDtm({
        ...geicoDtm,
        zip: quote.data.Zip,
        state: quote.data.StateCode,
        viewName: zipSelectionPageName,
      });
    }
  }, [quote.data.StateCode]);

  const setZipCode = (code: string): void => {
    const quoteData = { ...quote.data, Zip: code };
    store.dispatch(updateQuote(quoteData));
  };

  const setUnitedState = (code: string): void => {
    const quoteData = { ...quote.data, StateCode: code };
    store.dispatch(updateQuote(quoteData));
  };

  const onBlurZipCode = (value: string) => {
    let cleanedValue = digitsOnly(value);
    if (cleanedValue.length < 5) {
      setZipCodeError("Please enter a valid zip code.");
      setUnitedState("");
    } else {
      if (zipCode === "" && unitedState === "") {
        setZipCode(cleanedValue);
      }
      quote.data.LaunchpadId = uuidv4();
      store.dispatch(
        updateQuote({
          ...quote.data,
          Zip: cleanedValue,
          StateCode: quote.data.StateCode,
          CarrierDetail: [],
        })
      );
    }
  };

  const isNextButtonDisabled = (): boolean => {
    return (
      quote.data.Zip === undefined ||
      quote.data.Zip === "" ||
      quote.data.Zip.length !== 5 ||
      quote.data.StateCode === undefined ||
      quote.data.StateCode === "" ||
      quote.data.StateCode.length !== 2
    );
  };

  const stateLookup = async () => {
    if (zipCode.length === 5) {
      let request: StateLookupRequest = { zipCode: zipCode };
      showLoader();
      await api.commercialAgentApi
        .stateLookup(request)
        .then((response) => {
          if (response.type !== STATE_LOOKUP_RESPONSE) {
            window.scrollTo(0, 0);
            setShowAlert(true);
            setAlertMessage({
              title: "Thank you for your request.",
              message: API_ERROR_MESSAGE,
            });
          } else {
            if (!response.data.success) {
              const err =
                response.data.errorMessage === null || response.data.errorMessage === ""
                  ? "Please enter a valid zip code."
                  : response.data.errorMessage;
              setZipError(err);
            } else {
              setZipCodeError("");
              setUnitedState(response.data.state);
            }
          }
          hideLoader();
        })
        .catch((error) => {
          console.error(error);
          hideLoader();
        });
    } else {
      clearZipError();
    }
  };

  const setZipError = (errorMessage: string) => {
    refZipCodeUseRef.current?.focus();
    setZipCodeError(errorMessage);
    setUnitedState("");
    hideLoader();
  };

  const clearZipError = () => {
    setZipCodeError("");
    setUnitedState("");
  };

  return (
    <div>
      <GeolocationInput
        label="5-Digit ZIP Code"
        type="text"
        className=""
        containerClass=""
        style={{ marginTop: ".1rem", marginBottom: "0rem" }}
        labelClass="text"
        maxLength={5}
        value={zipCode}
        onBlur={(e) => onBlurZipCode(e.target.value)}
        onChange={(e) => {
          let cleanedValue = digitsOnly(e.target.value);
          setZipCode(cleanedValue);
        }}
        geoLocate={() => {
          if (refZipCodeUseRef.current) {
            setZipCode(refZipCodeUseRef.current?.value);
          }
        }}
        innerRef={refZipCodeUseRef}
        autoFocus
        error={zipCodeError}
      />
    </div>
  );
};